/**
 * ParcoursStubPage
 * ─────────────────────────────────────────────────────────────────
 * Template éditorial des 4 pages parcours rénovation Particulier :
 *  /particuliers/bilan-energetique
 *  /particuliers/audit-energetique
 *  /particuliers/renovation-globale
 *  /particuliers/mon-accompagnateur-renov
 *
 * Refonte 2026 : entièrement bâti sur le kit UI `audience/particulier`.
 * Conserve la side card spécifique `ParcoursLivePreview` (data-dense
 * par slug) qui n'a pas vocation à être génériquée.
 */

import { BreadcrumbListLd } from '../seo/jsonld/BreadcrumbList';
import {
  PARTICULIER_URLS,
  ParticulierAidesCrossLink,
  ParticulierCTAFinal,
  ParticulierFAQ,
  ParticulierKPIBand,
  ParticulierKeyPointsGrid,
  ParticulierPageHero,
  ParticulierParcoursNav,
  ParticulierVerticalStepper,
  type ParticulierKpi,
} from '../audience/particulier';
import { type ParcoursStub } from '../../config/parcours-particuliers';
import { ParcoursLivePreview } from './ParcoursLivePreview';

interface Props {
  parcours: ParcoursStub;
}

const SITE_URL = PARTICULIER_URLS.site;

const METRICS: Record<string, ParticulierKpi[]> = {
  'bilan-energetique': [
    {
      value: '0 €',
      label: "C'est gratuit, sans engagement",
      source: 'Aucune carte bancaire demandée',
    },
    {
      value: '5 min',
      numericValue: 5,
      suffix: ' min',
      label: 'Bilan complet en ligne',
      source: 'Sans déplacement · sans relevé compliqué',
    },
    {
      value: "Jusqu'à 90 %",
      label: "d'aides cumulables identifiées",
      source: 'Plafond ménages modestes · MPR + CEE + éco-PTZ + TVA + locales',
    },
  ],
  'audit-energetique': [
    {
      value: '850 €',
      numericValue: 850,
      suffix: ' €',
      label: "Coût moyen d'un audit",
      source: 'Fourchette 500 – 1 000 € selon la région',
    },
    {
      value: "Jusqu'à 500 €",
      numericValue: 500,
      suffix: ' €',
      label: 'remboursés par MaPrimeRénov’',
      source: 'Forfait Anah audit · selon vos revenus',
    },
    {
      value: '2 scénarios',
      label: 'Chiffrés minimum dans votre rapport',
      source: 'Étapes & rénovation complète',
    },
  ],
  'renovation-globale': [
    {
      value: '≥ 2 sauts',
      label: 'de classe DPE garantis',
      source: 'Ex. F → D ou G → E sur votre logement',
    },
    {
      value: "Jusqu'à 90%",
      label: "d'aides cumulables sur vos travaux",
      source: 'Plafond pour les ménages aux revenus modestes',
    },
    {
      value: 'Suivi MAR inclus',
      label: 'Un expert vous accompagne A à Z',
      source: "De l'audit au versement final",
    },
  ],
  'mon-accompagnateur-renov': [
    {
      value: '480 – 2 000 €',
      label: "Coût indicatif d'une mission MAR",
      source: 'Selon la mission · partiellement remboursé MPR Accompagné',
    },
    {
      value: 'Audit + suivi',
      label: 'Audit, plan, choix RGE, suivi chantier inclus',
      source: "Mission complète encadrée par l'Anah",
    },
    {
      value: 'Conseil neutre',
      label: 'Le MAR ne vend pas de travaux',
      source: 'Garantie Anah — pas de vente de travaux',
    },
  ],
};

export function ParcoursStubPage({ parcours }: Props) {
  const metrics = METRICS[parcours.slug] ?? [];

  return (
    <>
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: `${SITE_URL}/` },
          { name: 'Particuliers', url: `${SITE_URL}/particuliers` },
          { name: parcours.title, url: `${SITE_URL}/particuliers/${parcours.slug}` },
        ]}
      />

      <ParticulierPageHero
        breadcrumb={[{ name: parcours.title }]}
        eyebrow={parcours.eyebrow}
        eyebrowIconName={parcours.iconName}
        headline={parcours.title}
        subheadline={parcours.lead}
        primaryCta={{ label: parcours.ctaLabel, href: parcours.ctaHref }}
        secondaryCta={parcours.secondaryCta}
        bullets={
          parcours.heroBullets ?? [
            'Sources officielles citées',
            'Aucune relance commerciale',
            'Sans inscription pour démarrer',
          ]
        }
        microPromise={parcours.badgeLabel}
        sideSlot={<ParcoursLivePreview slug={parcours.slug} />}
      />

      {metrics.length > 0 && <ParticulierKPIBand items={metrics} />}

      <ParticulierKeyPointsGrid
        eyebrow="L'essentiel"
        title="Ce qu'il faut savoir avant de se lancer"
        points={parcours.keyPoints}
        cols={2}
        align="center"
      />

      <ParticulierVerticalStepper
        eyebrow="Déroulement"
        title="Comment ça se passe"
        description="Le déroulement présenté ci-contre est indicatif. Chaque situation est différente : caractéristiques du logement, revenus du foyer, urgence des travaux."
        steps={parcours.steps}
        cta={{ label: 'Calculer mes aides personnalisées', href: parcours.ctaHref }}
      />

      <ParticulierAidesCrossLink
        eyebrow="Aides mobilisables"
        title="Les 5 aides cumulables pour votre projet"
        description="MaPrimeRénov’, Prime énergie (CEE), éco-PTZ, TVA à 5,5 % et Coup de pouce peuvent se cumuler sur un même chantier."
        variant="compact"
      />

      <ParticulierParcoursNav
        currentSlug={parcours.slug}
        eyebrow="Parcours rénovation"
        title="Le parcours complet en 4 étapes"
        description="Du premier bilan jusqu'au versement des aides après travaux."
        variant="rich"
      />

      <ParticulierFAQ
        eyebrow={`FAQ ${parcours.shortLabel}`}
        title={`Vos questions sur ${parcours.shortLabel}.`}
        items={parcours.faq}
      />

      <ParticulierCTAFinal
        badge="Calcul des aides · 60 sec"
        title="Démarrez votre parcours"
        titleAccent="rénovation"
        description={`Toutes vos aides cumulables chiffrées en moins d'une minute. Sources officielles citées, aucune carte bancaire demandée — y compris pour ${parcours.shortLabel.toLowerCase()}.`}
        primaryCta={{ label: parcours.ctaLabel, href: parcours.ctaHref }}
        secondaryCta={{ label: 'Parler à un conseiller', href: PARTICULIER_URLS.contact }}
        bullets={['Aucune relance', 'Sources officielles', 'Résultat immédiat']}
      />
    </>
  );
}
